/**
 * 정산 파일 업로드/다운로드 공통 Hook
 */
import { ToastType } from "@/shared/components/feedback/Toast/Toast.types";
import { useToast } from "@/shared/components/feedback/Toast/ToastContext";
import { uploadSettlementFile, downloadExcelFile } from "@/shared/utils/settlementApiHelpers";
import { removeDateHyphens } from "@/shared/utils/dateUtils";

/**
 * 정산 파일 업로드/다운로드를 위한 공통 Hook
 */
export const useSettlementFile = (basePath: string) => {
    const { showToast } = useToast();

    /**
     * 엑셀 업로드
     */
    const uploadExcel = async (file: File, userId: string): Promise<boolean> => {
        try {
            const response = await uploadSettlementFile(basePath, file, userId);
            if (response.success) {
                showToast('정산 파일이 업로드되었습니다.', { type: ToastType.SUCCESS });
                return true;
            } else {
                showToast(response.message || '파일 업로드에 실패했습니다.', {
                    type: ToastType.ERROR
                });
                return false;
            }
        } catch {
            showToast('파일 업로드에 실패했습니다.', { type: ToastType.ERROR });
            return false;
        }
    };

    /**
     * 엑셀 다운로드 헬퍼
     * @param url 다운로드 URL
     * @param getFileName 파일명 생성 함수
     */
    const downloadExcel = async (
        url: string,
        getFileName: () => string
    ): Promise<void> => {
        try {
            const blob = await downloadExcelFile(url);
            const downloadUrl = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = downloadUrl;
            link.download = getFileName();
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(downloadUrl);
            showToast('엑셀 파일이 다운로드되었습니다.', { type: ToastType.SUCCESS });
        } catch {
            showToast('엑셀 다운로드에 실패했습니다.', { type: ToastType.ERROR });
        }
    };

    /**
     * 날짜 범위 기반 파일명 생성
     * @param startDate 시작일 (YYYY-MM-DD)
     * @param endDate 종료일 (YYYY-MM-DD)
     * @param prefix 파일명 접두사
     */
    const createDateRangeFileName = (
        startDate: string,
        endDate: string,
        prefix: string
    ): string => {
        const start = removeDateHyphens(startDate);
        const end = removeDateHyphens(endDate);
        return `${start}~${end}_${prefix}.xlsx`;
    };

    /**
     * 단일 날짜 기반 파일명 생성 (settlements 배열에서 날짜 추출)
     * @param settlements 정산 데이터 배열
     * @param dateField 날짜 필드명
     * @param prefix 파일명 접두사
     * @param fallbackDate 대체 날짜
     */
    const createSettlementFileName = <T extends Record<string, any>>(
        settlements: T[],
        dateFields: { start: keyof T; end: keyof T },
        prefix: string,
        fallbackDate?: string
    ): string => {
        if (settlements.length > 0) {
            const startDate = removeDateHyphens(String(settlements[0][dateFields.start]));
            const endDate = removeDateHyphens(String(settlements[0][dateFields.end]));
            return `${startDate}~${endDate}_${prefix}.xlsx`;
        }
        return fallbackDate
            ? `${prefix}_${removeDateHyphens(fallbackDate)}.xlsx`
            : `${prefix}.xlsx`;
    };

    return {
        uploadExcel,
        downloadExcel,
        createDateRangeFileName,
        createSettlementFileName
    };
};
